// The pieces the Mac huddle surfaces share (ios/SourcesMac/MacHuddle.swift): one person's FACE
// (speaking glow, raised hand, mute / sharing badge, a reaction drawn ON the face), the CONTROLS
// row (mic, camera + picker, share, react, hand, expand, leave — the same row in the panel and the
// window, so the two can never offer different controls), a live video element, the reactions
// rising over the stage, and the elapsed-time clock.
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { CallAudioOutputChoice, type AudioRouteKind } from '../../../core/callAudioOutput';
import { createPortal } from 'react-dom';
import type { CallParticipant } from '../../../core/callRoster';
import { CallEmoji } from '../../../core/callsCodec';
import { t } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { CallManager, callManager, type CallReactionBurst, type CallUiState } from './callManager';
import { canRouteAudio } from './callManagerCore';
import { CallIcon, type CallIconName } from './callIcons';
import { callInviter } from './callInvite';

export function personName(state: CallUiState, p: CallParticipant): string {
  if (p.sessionId === state.sessionId) return t('call.you');
  return state.people[p.userId]?.name ?? t('channelList.unknownUser');
}

/** "12:34" / "1:02:03", ticking — SwiftUI's `Text(date, style: .timer)`. */
export function useElapsed(startAt: number): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startAt]);
  const total = Math.max(0, Math.floor((now - startAt) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

export function HuddleFace({ state, participant, size, showsName = false, reaction }: { state: CallUiState; participant: CallParticipant; size: number; showsName?: boolean; reaction?: string | null }) {
  const isYou = participant.sessionId === state.sessionId;
  const person = state.people[participant.userId];
  const name = personName(state, participant);
  const vars = { '--face': `${size}px` } as CSSProperties;
  return (
    <span className={`huddle-face${participant.speaking ? ' speaking' : ''}`} style={vars} title={name} data-session={participant.sessionId}>
      <span className="huddle-face-disc">
        <Avatar userId={participant.userId} name={person?.name ?? (isYou ? t('call.you') : '?')} size={size} version={person?.version ?? 0} />
        {participant.raisedHandAt > 0 ? (
          <span className="huddle-face-hand" aria-label={t('call.hand.raise')}>
            ✋
          </span>
        ) : null}
        {reaction ? <span className="huddle-face-reaction">{reaction}</span> : null}
        {participant.muted || participant.sharingScreen ? (
          <span className={`huddle-face-badge${participant.sharingScreen ? ' sharing' : ''}`}>
            <CallIcon name={participant.sharingScreen ? 'screen' : 'micSlash'} size={Math.max(8, Math.round(size * 0.3))} strokeWidth={2.2} />
          </span>
        ) : null}
      </span>
      {showsName ? <span className="huddle-face-name">{name}</span> : null}
    </span>
  );
}

/** A live MediaStream on a <video>. Muted: call AUDIO plays through the manager's audio elements. */
export function StreamVideo({ stream, mirrored = false, fill = false, className }: { stream: MediaStream; mirrored?: boolean; fill?: boolean; className?: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    void el.play().catch(() => undefined);
  }, [stream]);
  return (
    <video
      ref={ref}
      className={className}
      autoPlay
      playsInline
      muted
      style={{ objectFit: fill ? 'cover' : 'contain', transform: mirrored ? 'scaleX(-1)' : undefined }}
    />
  );
}

/** A popover anchored ABOVE a control (the Mac's `arrowEdge: .top`), portalled out of any clip. */
function AnchoredPopover({ anchor, onClose, children, label }: { anchor: HTMLElement; onClose(): void; children: ReactNode; label: string }) {
  const rect = anchor.getBoundingClientRect();
  const ref = useRef<HTMLDivElement>(null);
  const [left, setLeft] = useState(rect.left);
  useLayoutEffect(() => {
    const width = ref.current?.offsetWidth ?? 0;
    const centred = rect.left + rect.width / 2 - width / 2;
    setLeft(Math.max(8, Math.min(window.innerWidth - width - 8, centred)));
  }, [rect.left, rect.width]);
  useEffect(() => {
    const close = (e: Event) => {
      if (ref.current?.contains(e.target as Node) || anchor.contains(e.target as Node)) return;
      onClose();
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('pointerdown', close, true);
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('pointerdown', close, true);
      window.removeEventListener('keydown', esc);
    };
  }, [anchor, onClose]);
  return createPortal(
    <div ref={ref} className="popover huddle-popover" role="dialog" aria-label={label} style={{ position: 'fixed', left, bottom: window.innerHeight - rect.top + 8 }}>
      {children}
    </div>,
    document.body,
  );
}

function ControlButton({ icon, help, tint, onClick, large, pressed, children, testId }: { icon: CallIconName; help: string; tint?: 'mention' | 'accent' | 'warning' | null; onClick(e: React.MouseEvent<HTMLButtonElement>): void; large: boolean; pressed?: boolean; children?: ReactNode; testId?: string }) {
  return (
    <button type="button" data-testid={testId} className={`huddle-control${tint ? ` tint-${tint}` : ''}${children ? ' with-label' : ''}`} onClick={onClick} title={help} aria-label={help} aria-pressed={pressed}>
      <CallIcon name={icon} size={large ? 18 : 15} strokeWidth={2} />
      {children}
    </button>
  );
}

/** The apps' route words (`Strings.Call.route*`). */
const ROUTE_KEYS: Record<AudioRouteKind, string> = { speaker: 'call.route.speaker', bluetooth: 'call.route.bluetooth', wired: 'call.route.wired' };

export function HuddleControls({ state, onExpand, large = false }: { state: CallUiState; onExpand?: () => void; large?: boolean }) {
  const [reactAnchor, setReactAnchor] = useState<HTMLElement | null>(null);
  const [cameraAnchor, setCameraAnchor] = useState<HTMLElement | null>(null);
  const [outputAnchor, setOutputAnchor] = useState<HTMLElement | null>(null);
  const active = state.phase === 'active';
  return (
    <div className={`huddle-controls${large ? ' large' : ''}`} role="toolbar" aria-label={t('call.window.title')}>
      <span className="huddle-camera-group">
        <ControlButton
          icon={state.muted ? 'micSlash' : 'mic'}
          help={state.micMissing ? t('web.call.noMicrophone') : state.muted ? t('call.unmute') : t('call.mute')}
          // Muted reads as the WARNING state, the way Slack's crossed mic does.
          tint={state.muted ? 'mention' : null}
          pressed={!state.muted}
          large={large}
          onClick={() => callManager.toggleMute()}
        />
        {/* Where the voices come out (P145, the apps' route picker): only where the browser can route. */}
        {CallAudioOutputChoice.offered(canRouteAudio(), state.audioOutputs) ? (
          <button
            type="button"
            className="huddle-camera-chevron"
            data-testid="call-audio-output"
            title={t('web.call.audioOutput')}
            aria-label={t('web.call.audioOutput')}
            onClick={(e) => {
              const target = e.currentTarget;
              void callManager.refreshAudioOutputs();
              setOutputAnchor(outputAnchor ? null : target);
            }}
          >
            <CallIcon name="chevronUp" size={10} strokeWidth={2.6} />
          </button>
        ) : null}
      </span>
      {state.videoAllowed ? (
        <span className="huddle-camera-group">
          <ControlButton
            icon={state.cameraOn ? 'video' : 'videoSlash'}
            help={state.cameraOn ? t('call.camera.off') : t('call.camera.on')}
            tint={state.cameraOn ? 'accent' : null}
            pressed={state.cameraOn}
            large={large}
            onClick={() => callManager.toggleCamera()}
          />
          {state.cameras.length > 1 ? (
            <button
              type="button"
              className="huddle-camera-chevron"
              title={t('call.camera.choose')}
              aria-label={t('call.camera.choose')}
              onClick={(e) => {
                const target = e.currentTarget;
                void callManager.refreshCameras();
                setCameraAnchor(cameraAnchor ? null : target);
              }}
            >
              <CallIcon name="chevronUp" size={10} strokeWidth={2.6} />
            </button>
          ) : null}
        </span>
      ) : null}
      <ControlButton
        icon={state.sharingScreen ? 'screenStop' : 'screen'}
        help={state.sharingScreen ? t('call.control.stopShare') : t('call.control.share')}
        tint={state.sharingScreen ? 'accent' : null}
        pressed={state.sharingScreen}
        large={large}
        onClick={() => (state.sharingScreen ? callManager.stopScreenShare() : callManager.requestScreenShare())}
      />
      <ControlButton icon="smile" help={t('call.react')} large={large} onClick={(e) => setReactAnchor(reactAnchor ? null : e.currentTarget)} />
      <ControlButton
        icon="hand"
        help={state.handRaised ? t('call.hand.lower') : t('call.hand.raise')}
        tint={state.handRaised ? 'warning' : null}
        pressed={state.handRaised}
        large={large}
        onClick={() => callManager.toggleRaiseHand()}
      />
      {/* «Invitar» (shared/API.md §30.3): only while this device is IN the call. The sidebar panel's
          row is full — there it is the "+" beside the faces (HuddlePanel). */}
      {active && large ? (
        <ControlButton icon="personAdd" help={t('web.call.invite.help')} large={large} onClick={() => callInviter.openPicker()} testId="call-invite">
          {large ? <span className="huddle-control-label">{t('web.call.invite')}</span> : null}
        </ControlButton>
      ) : null}
      {onExpand ? <ControlButton icon="expand" help={t('call.expand')} large={large} onClick={onExpand} /> : null}
      <span className="huddle-controls-spacer" />
      <button type="button" className="huddle-leave" onClick={() => callManager.leave()} title={t('call.leave')} aria-label={t('call.leave')}>
        <CallIcon name="phoneDown" size={large ? 18 : 15} filled strokeWidth={1.2} />
        {large ? <span>{t('call.leave.short')}</span> : null}
      </button>
      {reactAnchor && active ? (
        <AnchoredPopover anchor={reactAnchor} onClose={() => setReactAnchor(null)} label={t('call.react')}>
          {/* The webapp's own quick row, so a reaction sent from here is one a web user has too. */}
          <div className="huddle-reaction-picker">
            {CallEmoji.quickNames.map((name) => (
              <button
                key={name}
                type="button"
                title={`:${name}:`}
                aria-label={`:${name}:`}
                onClick={() => {
                  callManager.sendReaction(name);
                  setReactAnchor(null);
                }}
              >
                {CallEmoji.glyph(CallEmoji.fromName(name) ?? { name, unified: '', literal: '' })}
              </button>
            ))}
          </div>
        </AnchoredPopover>
      ) : null}
      {outputAnchor ? (
        <AnchoredPopover anchor={outputAnchor} onClose={() => setOutputAnchor(null)} label={t('web.call.audioOutput')}>
          <div className="huddle-camera-picker" data-testid="call-audio-output-picker">
            <div className="huddle-camera-title">{t('web.call.audioOutput')}</div>
            {[{ id: CallAudioOutputChoice.defaultId, name: t('web.call.audioOutput.default'), kind: 'speaker' as const }, ...state.audioOutputs].map((output) => (
              <button
                key={output.id}
                type="button"
                className="menu-item"
                onClick={() => {
                  callManager.selectAudioOutput(output.id);
                  setOutputAnchor(null);
                }}
              >
                <span style={{ opacity: output.id === state.audioOutputId ? 1 : 0, color: 'var(--accent)', display: 'grid' }}>
                  <CallIcon name="check" size={13} strokeWidth={2.4} />
                </span>
                <span className="huddle-camera-name">
                  {output.name}
                  {output.id !== CallAudioOutputChoice.defaultId ? <span className="huddle-output-kind"> · {t(ROUTE_KEYS[output.kind])}</span> : null}
                </span>
              </button>
            ))}
          </div>
        </AnchoredPopover>
      ) : null}
      {cameraAnchor ? (
        <AnchoredPopover anchor={cameraAnchor} onClose={() => setCameraAnchor(null)} label={t('call.camera.choose')}>
          <div className="huddle-camera-picker">
            <div className="huddle-camera-title">{t('call.camera.choose')}</div>
            {state.cameras.map((camera) => (
              <button
                key={camera.id}
                type="button"
                className="menu-item"
                onClick={() => {
                  callManager.selectCamera(camera.id);
                  setCameraAnchor(null);
                }}
              >
                <span style={{ opacity: camera.id === state.cameraId ? 1 : 0, color: 'var(--accent)', display: 'grid' }}>
                  <CallIcon name="check" size={13} strokeWidth={2.4} />
                </span>
                <span className="huddle-camera-name">{camera.name}</span>
              </button>
            ))}
          </div>
        </AnchoredPopover>
      ) : null}
    </div>
  );
}

/** Reactions rising and fading over the stage, each living `reactionLifetimeMs`, in lanes. */
export function HuddleReactionsOverlay({ reactions, state, large = false }: { reactions: CallReactionBurst[]; state: CallUiState; large?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    setWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);
  const laneWidth = large ? 128 : 34;
  const lanes = Math.max(1, Math.floor((width - laneWidth * 0.3) / laneWidth));
  return (
    <div className="huddle-reactions" ref={ref} aria-hidden="true">
      {reactions.map((burst, index) => {
        const sender = state.participants.find((p) => p.sessionId === burst.sessionId);
        const name = burst.sessionId === state.sessionId ? t('call.you') : state.people[burst.userId]?.name ?? (sender ? state.people[sender.userId]?.name : undefined);
        const age = Math.max(0, Date.now() - burst.at);
        return (
          <span
            key={burst.id}
            className={`huddle-reaction-bubble${large ? ' large' : ''}`}
            style={{ left: (index % lanes) * laneWidth, animationDelay: `-${age}ms`, animationDuration: `${CallManager.reactionLifetimeMs}ms` }}
          >
            <span className="glyph">{burst.glyph}</span>
            {large && name ? <span className="name">{name}</span> : null}
          </span>
        );
      })}
    </div>
  );
}
